import { formatDate, formatDateTime } from '@/utils/format';

const HIDDEN_COLUMN_KEYS = new Set([
  'organizationId',
  'orgId',
  'deleted',
  'createdBy',
  'updatedBy',
  'modifiedBy',
  'version',
]);

const PRIORITY_COLUMN_KEYS = [
  'id',
  'code',
  'name',
  'title',
  'status',
  'statusCode',
  'employeeId',
  'employeeName',
  'date',
  'createdAt',
];

const LABEL_KEYS = ['name', 'title', 'label', 'code', 'description', 'email'];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

function looksLikeJsonString(value: string): boolean {
  const trimmed = value.trim();
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  );
}

function formatIsoDateString(value: string): string {
  if (!ISO_DATE_RE.test(value) || Number.isNaN(Date.parse(value))) return value;
  return value.includes('T') ? formatDateTime(value) : formatDate(value);
}

/** camelCase / snake_case → readable column header */
export function humanizeColumnKey(key: string): string {
  return key
    .replace(/Json$/i, '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

/** Format any API value for display in table cells (never raw JSON blobs). */
export function formatTableCellValue(value: unknown): string {
  if (value == null || value === '') return '—';

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (looksLikeJsonString(trimmed)) {
      try {
        return formatTableCellValue(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return formatIsoDateString(trimmed);
  }

  if (value instanceof Date) {
    return formatDateTime(value.toISOString());
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (value.every((item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')) {
      return value.map((item) => formatTableCellValue(item)).join(', ');
    }
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }

  if (isPlainObject(value)) {
    for (const key of LABEL_KEYS) {
      const label = value[key];
      if (label != null && typeof label !== 'object') {
        return String(label);
      }
    }

    const status = value.statusCode ?? value.status;
    if (status != null && typeof status !== 'object') {
      return String(status);
    }

    const scalarEntries = Object.entries(value).filter(
      ([, entry]) => entry != null && typeof entry !== 'object',
    );

    if (scalarEntries.length === 0) {
      return `${Object.keys(value).length} field${Object.keys(value).length === 1 ? '' : 's'}`;
    }

    if (scalarEntries.length <= 4) {
      return scalarEntries
        .map(([key, entry]) => `${humanizeColumnKey(key)}: ${formatTableCellValue(entry)}`)
        .join(' · ');
    }

    return `${scalarEntries.length} values`;
  }

  return String(value);
}

export function normalizeRecordRows(data: unknown[] | null | undefined): Record<string, unknown>[] {
  if (!data?.length) return [];
  return data.filter(
    (row): row is Record<string, unknown> => row != null && typeof row === 'object' && !Array.isArray(row),
  );
}

export function inferRecordColumnKeys(
  rows: Record<string, unknown>[],
  excludeKeys: string[] = [],
): string[] {
  const excluded = new Set([...HIDDEN_COLUMN_KEYS, ...excludeKeys]);
  const keys = new Set<string>();

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!excluded.has(key)) keys.add(key);
    }
  }

  const ordered = [...keys];
  ordered.sort((a, b) => {
    const ai = PRIORITY_COLUMN_KEYS.indexOf(a);
    const bi = PRIORITY_COLUMN_KEYS.indexOf(b);
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
    return a.localeCompare(b);
  });

  return ordered;
}

export function recordRowKey(row: Record<string, unknown>, index: number, preferredKey = 'id'): string {
  const preferred = row[preferredKey];
  if (preferred != null && preferred !== '') return String(preferred);
  const code = row.code ?? row.name ?? row.title;
  if (code != null && code !== '') return String(code);
  return `row-${index}`;
}
